import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { formatApiErrorDetail } from "@/lib/utils-app";
import Logo from "@/components/app/Logo";

export default function RegisterPage() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/scan" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim()) { setError("Name is required"); return; }
    if (!phone.trim()) { setError("Phone number is required"); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (password !== confirmPassword) { setError("Passwords do not match"); return; }

    setLoading(true);
    try {
      await register(email, password, name, phone);
      navigate("/profile?setup=1", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="border-b-4 border-black p-6">
        <Logo size={44} showText className="text-3xl" />
        <p className="font-body text-sm text-neutral-500 mt-1">
          Thrift resale scouting
        </p>
      </div>

      <div className="flex-1 flex flex-col justify-center px-6 pb-12">
        <h2 className="font-heading text-2xl font-bold tracking-tight mb-6" data-testid="register-heading">
          Create Account
        </h2>

        <form onSubmit={handleSubmit} className="space-y-3">
          {error && (
            <div className="border-2 border-black bg-[#FF1744] text-white p-3 font-body text-sm font-medium" data-testid="register-error">
              {error}
            </div>
          )}

          <div>
            <label className="block font-body text-xs font-semibold tracking-[0.1em] uppercase mb-1.5">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border-2 border-black bg-white p-3.5 font-body text-base focus:outline-none focus:ring-4 focus:ring-black/20"
              required
              data-testid="register-name-input"
            />
          </div>

          <div>
            <label className="block font-body text-xs font-semibold tracking-[0.1em] uppercase mb-1.5">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border-2 border-black bg-white p-3.5 font-body text-base focus:outline-none focus:ring-4 focus:ring-black/20"
              required
              data-testid="register-email-input"
            />
          </div>

          <div>
            <label className="block font-body text-xs font-semibold tracking-[0.1em] uppercase mb-1.5">Cell Phone</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 123-4567"
              className="w-full border-2 border-black bg-white p-3.5 font-body text-base focus:outline-none focus:ring-4 focus:ring-black/20"
              required
              data-testid="register-phone-input"
            />
          </div>

          <div>
            <label className="block font-body text-xs font-semibold tracking-[0.1em] uppercase mb-1.5">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border-2 border-black bg-white p-3.5 font-body text-base focus:outline-none focus:ring-4 focus:ring-black/20"
              required
              minLength={6}
              data-testid="register-password-input"
            />
          </div>

          <div>
            <label className="block font-body text-xs font-semibold tracking-[0.1em] uppercase mb-1.5">Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full border-2 border-black bg-white p-3.5 font-body text-base focus:outline-none focus:ring-4 focus:ring-black/20"
              required
              minLength={6}
              data-testid="register-confirm-password-input"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-black text-white p-4 font-heading font-bold text-lg uppercase tracking-wide border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:translate-y-[2px] active:translate-x-[2px] active:shadow-none transition-all disabled:opacity-50"
            data-testid="register-submit-btn"
          >
            {loading ? "Creating account..." : "Create Account"}
          </button>
        </form>

        <p className="mt-6 text-center font-body text-sm">
          Already have an account?{" "}
          <Link to="/login" className="font-semibold underline underline-offset-4" data-testid="register-login-link">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
